import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/styles';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { getFormValues, SubmissionError } from 'redux-form';
import { withRouter } from 'react-router-dom';
import Dialog from 'components/modals/Dialog';
import DialogTitle from 'components/modals/DialogTitle';
import ConfirmationModal from 'components/modals/ConfirmationModal';
import Button from 'components/buttons/Button';
import { urlCampaignById } from 'constants/apiRouting';
import PlusCircleOutlineIcon from 'mdi-react/PlusCircleOutlineIcon';
import { fade } from '@material-ui/core/styles/colorManipulator';
import {
  createCampaign,
  setCampaignBuilderOpen,
  setSequenceBuilderOpen,
} from 'redux/actions/campaignActions';
import { clearSequenceStore } from 'redux/actions/sequenceActions';
import { clearSchedulesStore } from 'redux/actions/scheduleActions';
import CampaignForm from './CampaignForm';


const styles = theme => ({
  createButtonText: {
    color: theme.palette.primary.main,
    backgroundColor: fade(theme.palette.primary.main, 0.2),
    width: '100%',
    '&:hover': {
      backgroundColor: fade(theme.palette.primary.main, 0.25),
      color: theme.palette.primary.main,
    },
    '&:active': {
      backgroundColor: fade(theme.palette.primary.main, 0.25),
      color: theme.palette.primary.main,
    },
    '&.collapsed': {
      minWidth: '4.5rem !important',
    },
  },
});

class CampaignCreate extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    classes: PropTypes.shape.isRequired,
    collapsed: PropTypes.bool,
    isOpen: PropTypes.bool.isRequired,
    setCampaignBuilderOpen: PropTypes.func.isRequired,
    createCampaign: PropTypes.func.isRequired,
    campaign: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    user: PropTypes.shape({
      pk: PropTypes.number,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      isAuthenticated: PropTypes.bool,
      user_groups: PropTypes.array,
      organizations: PropTypes.array,
    }).isRequired,
    formValues: PropTypes.shape(PropTypes.any).isRequired,
    setSequenceBuilderOpen: PropTypes.func.isRequired,
    clearSequenceStore: PropTypes.func.isRequired,
    clearSchedulesStore: PropTypes.func.isRequired,
  };

  static defaultProps = {
    className: '',
    collapsed: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      areYouSureModalIsOpen: false,
    };

    this.toggle = this.toggle.bind(this);
  }

  async onSubmit(formData) {
    try {
      await this.props.createCampaign({
        name: formData.name,
        account: formData.account.value,
        email_service_provider_configs: formData.email_service_provider_configs.map((config) => {
          const emailSettings = formData[config.value];
          return {
            id: config.value,
            ...emailSettings,
          };
        }),
        launch_date: formData.launch_date,
        campaign_manager: formData.campaign_manager
          ? formData.campaign_manager.value
          : this.props.user.pk,
        user_groups: this.props.user.user_groups,
        organizations: this.props.user.organizations.map(o => o.name),
      });
      // Check if campaign was successfully created and campaign.id exists.
      if (this.props.campaign.id) {
        this.props.setCampaignBuilderOpen(false);
        if (formData.next) {
          this.props.setSequenceBuilderOpen(true);
          this.props.clearSequenceStore();
          this.props.clearSchedulesStore();
        }
        this.props.history.push(urlCampaignById(this.props.campaign.id));
      }
    } catch (error) {
      if (error.errors) {
        throw new SubmissionError(error.errors);
      }
    }
  }

  toggle() {
    if (this.hasChanged()) {
      this.setState({ areYouSureModalIsOpen: true });
    } else {
      this.props.setCampaignBuilderOpen(!this.props.isOpen);
    }
  }

  toggleAreYouSure(answer) {
    if (answer) {
      this.setState({ areYouSureModalIsOpen: false }, () => this.props.setCampaignBuilderOpen(false));
    } else {
      this.setState({ areYouSureModalIsOpen: false });
    }
  }

  hasChanged = () => this.props.formValues &&
    (this.props.formValues.name || this.props.formValues.account || this.props.formValues.campaign_manager);

  render() {
    const {
      classes,
      collapsed,
      isOpen,
    } = this.props;
    return (
      <div className={this.props.className}>
        <Button
          color="primary"
          classes={{ root: `${classes.createButtonText} ${collapsed ? 'collapsed' : ''}` }}
          variant="contained"
          onClick={this.toggle}
          size="large"
          startIcon={<PlusCircleOutlineIcon />}
          data-intercom-target="Add campaign button"
        >
          {collapsed ? '' : 'Create Campaign'}
        </Button>

        <Dialog open={isOpen} onBackdropClick={this.toggle}>
          <DialogTitle id="dialog-title" disableTypography>
            Create campaign
          </DialogTitle>
          <CampaignForm addBtnText="Next" toggleModal={this.toggle} onSubmit={data => this.onSubmit(data)} />
        </Dialog>
        <ConfirmationModal
          open={this.state.areYouSureModalIsOpen}
          onBackdropClick={() => this.toggleAreYouSure(false)}
          title="Confirm"
          confirmLabel="Yes"
          cancelLabel="No"
          onConfirmClick={() => this.toggleAreYouSure(true)}
          onCancelClick={() => this.toggleAreYouSure(false)}
        >
          All changes will be lost. <br />Are you sure want to close?
        </ConfirmationModal>
      </div>
    );
  }
}

export default compose(
  withRouter,
  withStyles(styles),
  connect(
    state => ({
      campaign: state.campaign,
      user: state.user,
      formValues: getFormValues('campaignCreateForm')(state),
      isOpen: state.createCampaignState.openCampaignBuilder,
    }),
    {
      createCampaign,
      setCampaignBuilderOpen,
      setSequenceBuilderOpen,
      clearSequenceStore,
      clearSchedulesStore,
    },
  ),
  withNamespaces('common'),
)(CampaignCreate);
